import { useState, useContext } from "react";
import { PublicKey } from "@solana/web3.js";
import { useConnection } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import * as dripIdl from "../idl/drip_vault_v2.json";
import { DripVaultDescriptor } from "../constants/dripVaults";
import { secondsBetweenSlots } from "../helpers/slotTime";
import {
  DripPosition,
  dripProgramFor,
  fetchDripPositionCore,
  fetchSolPrice,
  solValueOf,
} from "../features/drip/dripFetch";

// Re-exported so existing importers keep working.
export type { DripPosition };

export const useDripPosition = (vault: DripVaultDescriptor) => {
  const { connection } = useConnection();
  const { dripProgramAddress } = useContext(AppConfigContext);

  const [position, setPosition] = useState<DripPosition | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPosition, setHasPosition] = useState(false);

  const fetchPosition = async (publicKey: PublicKey) => {
    setIsLoading(true);

    try {
      const programId = new PublicKey(dripProgramAddress);
      const program = dripProgramFor(dripIdl, dripProgramAddress, connection);

      const core = await fetchDripPositionCore(
        program,
        connection,
        vault,
        programId,
        publicKey
      );

      if (!core) {
        setHasPosition(false);
        setPosition(null);
        return;
      }

      // Warm-up: a deposit made this epoch has shares that are still pending
      // and earns nothing until the swapper's next harvest. Withdrawable
      // throughout — this only drives the rewards countdown.
      const epochInfo = await connection.getEpochInfo();
      const isWarmingUp =
        core.activeSharesRaw === 0 &&
        core.pendingSharesRaw > 0 &&
        core.lastDepositEpoch === Number(epochInfo.epoch);
      // A warming-up deposit waits out one further full epoch.
      const nextEpochStartSlot = epochInfo.absoluteSlot + Math.max(0, epochInfo.slotsInEpoch - epochInfo.slotIndex);
      const targetSlot = isWarmingUp
        ? nextEpochStartSlot + epochInfo.slotsInEpoch
        : nextEpochStartSlot;
      const nextEpochEstimateSeconds =
        Math.round(secondsBetweenSlots(epochInfo.absoluteSlot, targetSlot, epochInfo.slotsInEpoch)) +
        2 * 60 * 60;

      // An unavailable share supply leaves tokenValue at 0, which must not be
      // priced — "~$0.00" would read as an empty position.
      const solPrice = await fetchSolPrice();
      const solValue = solValueOf(vault, core.tokenValue, core.rate);
      const tokenValueUsd =
        solPrice != null && !core.isValueUnavailable ? solValue * solPrice : null;

      setHasPosition(true);
      setPosition({
        ...core,
        tokenValueUsd,
        nextEpochEstimateSeconds,
        isWarmingUp,
      });
    } catch (err) {
      console.error(`Error fetching drip position (${vault.id}):`, err);
      setHasPosition(false);
      setPosition(null);
    } finally {
      setIsLoading(false);
    }
  };

  return { position, isLoading, hasPosition, fetchPosition };
};
